import { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { X, Send, Loader2 } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;
const MASCOT_URL = 'https://i.ibb.co/202m7nzj/flying-mascot-B1h-FSLk0.gif';
const STORAGE_KEY = 'dotcall_chatbot_session_id';
const MSGS_KEY = 'dotcall_chatbot_messages';

function getOrCreateSession() {
  let sid = sessionStorage.getItem(STORAGE_KEY);
  if (!sid) {
    sid = 'chat_' + Math.random().toString(36).slice(2) + Date.now().toString(36);
    sessionStorage.setItem(STORAGE_KEY, sid);
  }
  return sid;
}

const INITIAL_BOT_MESSAGE = {
  role: 'assistant',
  content: "Hi there! I'm DotCall AI Chat. Ask me about our AI voice agents, pricing, or Indian-language support — I'm happy to help!"
};

export default function DotCallChatbot() {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState(() => {
    try {
      const stored = sessionStorage.getItem(MSGS_KEY);
      if (stored) return JSON.parse(stored);
    } catch (e) {}
    return [INITIAL_BOT_MESSAGE];
  });
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);
  const [unread, setUnread] = useState(0);
  const [leadSaved, setLeadSaved] = useState(false);
  const scrollRef = useRef(null);
  const sessionId = useRef(getOrCreateSession());

  // Persist messages to session storage
  useEffect(() => {
    try { sessionStorage.setItem(MSGS_KEY, JSON.stringify(messages)); } catch (e) {}
  }, [messages]);

  // Auto-scroll to bottom
  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages, open]);

  // Clear unread when opening
  useEffect(() => { if (open) setUnread(0); }, [open]);

  const sendMessage = async (e) => {
    e?.preventDefault();
    const msg = input.trim();
    if (!msg || sending) return;
    setInput('');
    const next = [...messages, { role: 'user', content: msg }];
    setMessages(next);
    setSending(true);
    try {
      const res = await axios.post(`${API}/api/chatbot/message`, {
        session_id: sessionId.current,
        message: msg,
      });
      const reply = res.data.reply || 'Sorry, I could not generate a response. Try again?';
      setMessages(m => [...m, { role: 'assistant', content: reply }]);
      if (res.data.lead_captured) setLeadSaved(true);
      if (!open) setUnread(u => u + 1);
    } catch (err) {
      setMessages(m => [...m, { role: 'assistant', content: "I'm having a quick hiccup. Please try that again in a moment." }]);
    }
    setSending(false);
  };

  const SUGGESTIONS = [
    'What can DotCall do?',
    'How much does it cost?',
    'Does it speak Hindi?',
    'Tell me about e-commerce calls',
  ];

  return (
    <>
      {/* Floating Mascot Button (bottom-right) - Hidden when chat is open */}
      {!open && (
        <button
          type="button"
          onClick={() => setOpen(true)}
          className="fixed bottom-5 right-5 z-[60] focus:outline-none group"
          aria-label="Open DotCall AI Chat"
          data-testid="chatbot-fab"
        >
          <div className="relative">
            {/* Animated background layers */}
            <span className="absolute inset-0 rounded-full bg-gradient-to-tr from-amber-500/35 via-orange-400/25 to-amber-300/15 blur-xl chatbot-pulse-glow" aria-hidden="true" />
            <span className="absolute inset-[-6px] rounded-full border border-amber-400/30 chatbot-ring-rotate" aria-hidden="true" />
            <span className="absolute inset-[-14px] rounded-full border border-amber-500/15 chatbot-ring-rotate-rev" aria-hidden="true" />
            <span className="absolute -inset-1 rounded-full bg-amber-500/10 chatbot-ping" aria-hidden="true" />

            {/* Mascot GIF in circular container - FLIPPED to look left */}
            <div className="relative w-[88px] h-[88px] rounded-full bg-gradient-to-br from-[#0B1221] via-[#18223A] to-[#0B1221] p-1.5 shadow-[0_10px_40px_rgba(245,158,11,0.35)] group-hover:shadow-[0_10px_50px_rgba(245,158,11,0.55)] transition-all">
              <div className="w-full h-full rounded-full overflow-hidden bg-[#0B1221] flex items-center justify-center">
                <img src={MASCOT_URL} alt="DotCall AI mascot" className="w-full h-full object-cover scale-x-[-1]" />
              </div>
              {/* online dot */}
              <span className="absolute bottom-1.5 right-1.5 w-3.5 h-3.5 rounded-full bg-emerald-400 border-2 border-[#0B1221] animate-pulse" />
            </div>

            {/* Unread bubble */}
            {unread > 0 && (
              <span className="absolute -top-1 -right-1 min-w-[22px] h-[22px] px-1.5 rounded-full bg-red-500 text-white text-[11px] font-bold flex items-center justify-center shadow-lg">{unread}</span>
            )}

            {/* Tooltip label (when closed) - Now on LEFT side */}
            <div className="hidden md:flex absolute right-[100px] top-1/2 -translate-y-1/2 items-center gap-2 px-3 py-1.5 rounded-full bg-[#0B1221] border border-amber-500/25 text-white text-xs font-medium whitespace-nowrap shadow-lg chatbot-tooltip opacity-0 group-hover:opacity-100 transition-opacity">
              <span className="w-1.5 h-1.5 rounded-full bg-emerald-400" />
              Chat with DotCall AI
            </div>
          </div>
        </button>
      )}

      {/* Chat Panel */}
      {open && (
        <div className="fixed bottom-5 right-5 z-[59] w-[calc(100vw-2.5rem)] sm:w-[380px] max-w-[380px] h-[560px] max-h-[calc(100vh-140px)] rounded-2xl bg-[#0B1221] border border-white/[0.08] shadow-[0_20px_60px_rgba(0,0,0,0.55)] flex flex-col overflow-hidden chatbot-panel-in" data-testid="chatbot-panel">
          {/* Header */}
          <div className="flex items-center justify-between px-4 py-3 bg-gradient-to-r from-amber-600/20 via-amber-500/10 to-transparent border-b border-white/[0.06]">
            <div className="flex items-center gap-2.5">
              <div className="relative w-9 h-9 rounded-full overflow-hidden bg-[#0B1221] flex-shrink-0 border border-amber-500/30">
                <img src={MASCOT_URL} alt="" className="w-full h-full object-cover scale-x-[-1]" />
                <span className="absolute bottom-0 right-0 w-2.5 h-2.5 rounded-full bg-emerald-400 border-2 border-[#0B1221]" />
              </div>
              <div>
                <div className="text-sm font-bold text-white leading-tight">DotCall AI Chat</div>
                <div className="text-[10px] text-emerald-400 flex items-center gap-1"><span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" /> Online</div>
              </div>
            </div>
            <button type="button" onClick={() => setOpen(false)} className="text-white/40 hover:text-white transition-colors" aria-label="Close chat" data-testid="chatbot-close">
              <X className="w-4 h-4" />
            </button>
          </div>

          {/* Messages */}
          <div ref={scrollRef} className="flex-1 overflow-y-auto px-4 py-4 space-y-3 chatbot-scroll">
            {messages.map((m, i) => (
              <div key={i} className={`flex ${m.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                <div className={`max-w-[85%] px-3.5 py-2.5 rounded-2xl text-[13px] leading-snug whitespace-pre-wrap ${
                  m.role === 'user'
                    ? 'bg-amber-600 text-white rounded-br-sm'
                    : 'bg-white/[0.06] text-white/90 border border-white/[0.06] rounded-bl-sm'
                }`}>
                  {m.content}
                </div>
              </div>
            ))}
            {sending && (
              <div className="flex justify-start">
                <div className="px-3.5 py-2.5 rounded-2xl bg-white/[0.06] border border-white/[0.06] rounded-bl-sm">
                  <span className="flex gap-1">
                    <span className="w-1.5 h-1.5 rounded-full bg-white/40 chatbot-typing-dot" />
                    <span className="w-1.5 h-1.5 rounded-full bg-white/40 chatbot-typing-dot" style={{animationDelay: '0.15s'}} />
                    <span className="w-1.5 h-1.5 rounded-full bg-white/40 chatbot-typing-dot" style={{animationDelay: '0.3s'}} />
                  </span>
                </div>
              </div>
            )}
            {leadSaved && (
              <div className="mx-auto max-w-[90%] px-3 py-2 rounded-lg bg-emerald-600/15 border border-emerald-500/25 text-emerald-300 text-[11px] text-center">
                ✓ Our team will be in touch shortly.
              </div>
            )}
          </div>

          {/* Suggestions (only before first user message) */}
          {messages.length <= 1 && (
            <div className="px-3 pb-2 flex flex-wrap gap-1.5">
              {SUGGESTIONS.map(s => (
                <button
                  key={s}
                  type="button"
                  onClick={() => { setInput(s); }}
                  className="text-[11px] px-2.5 py-1.5 rounded-full bg-white/[0.04] border border-white/[0.08] text-white/70 hover:bg-white/[0.08] hover:text-white transition-colors"
                  data-testid={`chatbot-suggest-${s.slice(0,10)}`}
                >
                  {s}
                </button>
              ))}
            </div>
          )}

          {/* Input */}
          <form onSubmit={sendMessage} className="border-t border-white/[0.06] px-3 py-3 flex items-center gap-2 bg-[#0B1221]">
            <input
              type="text"
              value={input}
              onChange={e => setInput(e.target.value)}
              placeholder="Type your message..."
              maxLength={500}
              disabled={sending}
              className="flex-1 bg-white/[0.04] border border-white/[0.08] rounded-full px-4 py-2 text-sm text-white placeholder:text-white/30 focus:outline-none focus:border-amber-500/40 disabled:opacity-50"
              data-testid="chatbot-input"
              autoFocus
            />
            <button
              type="submit"
              disabled={!input.trim() || sending}
              className="w-9 h-9 rounded-full bg-amber-600 hover:bg-amber-500 text-white flex items-center justify-center disabled:opacity-40 disabled:cursor-not-allowed flex-shrink-0"
              data-testid="chatbot-send"
            >
              {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
            </button>
          </form>
          <div className="px-3 pb-2 text-center text-[10px] text-white/25">Powered by DotCall AI · <a href="/contact" className="underline hover:text-white/50">Prefer email?</a></div>
        </div>
      )}

      <style>{`
        @keyframes chatbot-pulse-glow {
          0%, 100% { opacity: 0.7; transform: scale(1); }
          50% { opacity: 1; transform: scale(1.15); }
        }
        .chatbot-pulse-glow { animation: chatbot-pulse-glow 2.4s ease-in-out infinite; }

        @keyframes chatbot-ring-rotate {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
        .chatbot-ring-rotate { animation: chatbot-ring-rotate 10s linear infinite; }
        .chatbot-ring-rotate-rev { animation: chatbot-ring-rotate 18s linear infinite reverse; }

        @keyframes chatbot-ping {
          0% { transform: scale(0.9); opacity: 0.7; }
          70%, 100% { transform: scale(1.7); opacity: 0; }
        }
        .chatbot-ping { animation: chatbot-ping 2.6s cubic-bezier(0,0,0.2,1) infinite; }

        @keyframes chatbot-panel-in {
          from { opacity: 0; transform: translateY(14px) scale(0.97); }
          to { opacity: 1; transform: translateY(0) scale(1); }
        }
        .chatbot-panel-in { animation: chatbot-panel-in 0.25s ease-out; }

        @keyframes chatbot-typing-dot {
          0%, 60%, 100% { transform: translateY(0); opacity: 0.4; }
          30% { transform: translateY(-4px); opacity: 1; }
        }
        .chatbot-typing-dot { display: inline-block; animation: chatbot-typing-dot 1s ease-in-out infinite; }

        .chatbot-scroll::-webkit-scrollbar { width: 4px; }
        .chatbot-scroll::-webkit-scrollbar-track { background: transparent; }
        .chatbot-scroll::-webkit-scrollbar-thumb { background: rgba(255,255,255,0.1); border-radius: 2px; }

        .chatbot-tooltip { animation: chatbot-panel-in 0.2s ease-out; }
      `}</style>
    </>
  );
}
